/**
 * TalentRow molecule — Avatar + name fragment used in the pool table Name cell
 * and reused by AddFromPoolModal (plan 04) for consistent name rendering.
 *
 * This component composes the identity portion of a talent row so both surfaces
 * render the same Avatar + name + email layout without duplication.
 */

import React from 'react';
import { Avatar } from '@/components/ui/atoms/Avatar';
import { Typography } from '@/components/ui/atoms/Typography';

export interface TalentRowNameProps {
  /** Full display name */
  fullName: string;
  /** Two-letter initials for Avatar fallback */
  initials: string;
  /** Optional avatar image URL */
  avatarUrl?: string;
  /** Optional secondary line (email or title) */
  secondary?: string;
  /** Avatar size — sm for table rows, md for modals */
  avatarSize?: 'sm' | 'md';
}

/**
 * TalentRow — identity fragment (Avatar + name + optional secondary line).
 * Used in the pool table Name column cell and in AddFromPoolModal talent rows.
 */
export const TalentRow: React.FC<TalentRowNameProps> = ({
  fullName,
  initials,
  avatarUrl,
  secondary,
  avatarSize = 'sm',
}) => {
  return (
    <div className="flex items-center gap-2">
      <Avatar
        src={avatarUrl}
        initials={initials}
        alt={fullName}
        size={avatarSize}
        variant="neutral"
      />
      <div className="min-w-0">
        <Typography variant="body-sm" weight="semibold" color="primary">
          {fullName}
        </Typography>
        {secondary && (
          <Typography variant="body-sm" weight="medium" color="muted" className="truncate">
            {secondary}
          </Typography>
        )}
      </div>
    </div>
  );
};

TalentRow.displayName = 'TalentRow';
